import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { Plus, Zap } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { EntityList, type EntityListColumn } from '@/components/crm/entity-list'
import { useServerList } from '@/components/data-table/use-server-list'
import { PageHeader } from '@/components/layout/page-header'
import { useEntityListNav } from '@/components/layout/use-entity-nav'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/custom-modules/$key/workflows/')({
  component: WorkflowsListPage,
})

// EntityList sleutelt rijen op `id`; de detailroute is `.../workflows/$workflowKey`
// en de recordnavigatie levert sleutels terug (zie `idCol` in
// custom-workflows.router.ts). Daarom is `id` hier de workflowSLEUTEL.
interface WorkflowRow {
  id: string
  key: string
  name: string
  triggerLabel: string
  stepCount: number
  enabled: boolean
  disabledReason: string | null
}

function WorkflowsListPage() {
  const { key: moduleKey } = Route.useParams()
  const t = useTranslations()
  const navigate = useNavigate()

  // De module is de afbakening van deze lijst, en reist als filter mee: de
  // detailpagina leest hem terug uit `useListFilter` en stuurt hem in zijn
  // `neighbors`-aanroep. Zie `workflowFilterWhere` in custom-workflows.router.ts.
  const filters = useMemo(() => ({ moduleKey }), [moduleKey])

  const list = useServerList({
    entityKey: 'customWorkflow',
    initialSorting: [{ id: 'name', desc: false }],
    filters,
  })
  const workflowsQ = useQuery(
    orpc.modules.custom.workflows.list.queryOptions({ input: { ...list.input, ...filters } }),
  )
  const rows = useMemo<WorkflowRow[]>(
    () =>
      (workflowsQ.data?.rows ?? []).map((wf) => {
        const trigger = wf.trigger as { type?: string; entityKey?: string }
        const triggerLabel = trigger.entityKey
          ? `${trigger.type ?? '—'} · ${trigger.entityKey}`
          : (trigger.type ?? '—')
        return {
          id: wf.key,
          key: wf.key,
          name: wf.name,
          triggerLabel,
          stepCount: (wf.steps as unknown[]).length,
          enabled: wf.enabled,
          disabledReason: wf.disabledReason,
        }
      }),
    [workflowsQ.data],
  )
  const total = workflowsQ.data?.total
  useEffect(() => {
    list.setTotal(total)
  }, [total, list.setTotal])

  useEntityListNav({
    entityKey: 'customWorkflow',
    total: total ?? 0,
    toNew: () =>
      navigate({ to: '/settings/custom-modules/$key/workflows/new', params: { key: moduleKey } }),
    search: list.search,
    onSearch: list.setSearch,
    sort: list.sort,
  })

  // Elke kolom met een sorteerpijl (`sortAccessor`) staat ook in `sortColumns` van
  // custom-workflows.router.ts. `triggerLabel` en `stepCount` staan niet in een kolom
  // maar in JSONB; de router sorteert ze met dezelfde uitdrukking als de cel toont,
  // zodat de pijl kan blijven staan.
  const columns = useMemo<EntityListColumn<WorkflowRow>[]>(
    () => [
      {
        key: 'name',
        header: t('builderRuntime.workflows.col.name'),
        render: (r) => <span className="font-medium">{r.name}</span>,
        sortAccessor: (r) => r.name,
      },
      {
        key: 'triggerLabel',
        header: t('builderRuntime.workflows.col.trigger'),
        render: (r) => (
          <span className="font-mono text-xs text-muted-foreground">{r.triggerLabel}</span>
        ),
        sortAccessor: (r) => r.triggerLabel,
      },
      {
        key: 'stepCount',
        header: t('builderRuntime.workflows.col.steps'),
        render: (r) => <span className="tabular-nums">{r.stepCount}</span>,
        sortAccessor: (r) => r.stepCount,
      },
      {
        key: 'enabled',
        header: t('builderRuntime.workflows.col.status'),
        render: (r) =>
          r.enabled ? (
            <Badge variant="default">{t('builderRuntime.workflows.status.enabled')}</Badge>
          ) : r.disabledReason ? (
            <Badge variant="destructive">{t('builderRuntime.workflows.status.autoPaused')}</Badge>
          ) : (
            <Badge variant="secondary">{t('builderRuntime.workflows.status.disabled')}</Badge>
          ),
        sortAccessor: (r) => String(r.enabled),
      },
    ],
    [t],
  )

  return (
    <>
      <PageHeader
        title={t('builderRuntime.workflows.title')}
        subtitle={t('builderRuntime.workflows.subtitle')}
        actions={
          <Button asChild>
            <Link to="/settings/custom-modules/$key/workflows/new" params={{ key: moduleKey }}>
              <Plus className="h-4 w-4 mr-2" />
              {t('builderRuntime.workflows.addAction')}
            </Link>
          </Button>
        }
      />
      <EntityList<WorkflowRow>
        rows={rows}
        columns={columns}
        isLoading={workflowsQ.isLoading}
        emptyState={
          <div className="flex flex-col items-center gap-2">
            <Zap className="h-8 w-8 text-muted-foreground opacity-40" />
            <h3 className="font-medium">{t('builderRuntime.workflows.empty.title')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('builderRuntime.workflows.empty.subtitle')}
            </p>
            <Button asChild className="mt-2">
              <Link to="/settings/custom-modules/$key/workflows/new" params={{ key: moduleKey }}>
                <Plus className="h-4 w-4 mr-2" />
                {t('builderRuntime.workflows.empty.cta')}
              </Link>
            </Button>
          </div>
        }
        server={list.server}
        onRowClick={(r) =>
          navigate({
            to: '/settings/custom-modules/$key/workflows/$workflowKey',
            params: { key: moduleKey, workflowKey: r.key },
          })
        }
      />
    </>
  )
}
